import React, { useState, useEffect, useRef } from "react";
import "./Mission.css";

// Web-sized copies of the originals live in /images/slideshow
const slides = [
  // 2026–27 season
  {
    image: "/images/slideshow/bowling-group.jpg",
    duration: 2000,
    alt: "2026–27 team at the season kickoff",
  },
  {
    image: "/images/slideshow/glorious-pipes.jpg",
    duration: 2000,
    alt: "24-inch steel pipes from Westbrook Metals",
  },
  // 2025–26 season
  {
    image: "/images/slideshow/sunshot.jpg",
    duration: 2000,
    alt: "Liora famous sunshot",
  },
  {
    image: "/images/slideshow/watermelon-meeting.jpg",
    duration: 1500,
    alt: "UATXcavators Watermelon Meeting",
  },
  {
    image: "/images/slideshow/prototype-with-flag.jpg",
    duration: 2000,
    alt: "Prototype with American Flag",
  },
  {
    video: "/images/slideshow/prototype-digging.mp4",
    poster: "/images/slideshow/prototype-digging-poster.jpg",
    duration: 3000,
    alt: "Prototype Digging",
  },
  {
    image: "/images/slideshow/website-building.jpg",
    duration: 2000,
    alt: "Website Building Slide 1",
  },
  {
    image: "/images/slideshow/electrical-panel.jpg",
    duration: 2000,
    alt: "UATXcavators Electrical Panel",
  },
  {
    image: "/images/slideshow/judah-weld.jpg",
    duration: 2000,
    alt: "Judah Welding",
  },
  {
    image: "/images/slideshow/judah-cutterhead.jpg",
    duration: 2000,
    alt: "Judah rigging the cutterhead on the engine hoist",
  },
  {
    image: "/images/slideshow/sparky.jpg",
    duration: 2000,
    alt: "sparky",
  },
];

// Parallax only on larger screens; on phones it costs more than it adds
const MOTION_QUERY =
  "(min-width: 769px) and (prefers-reduced-motion: no-preference)";

const Mission = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const sectionRef = useRef(null);
  const backgroundRef = useRef(null);
  const videoRefs = useRef([]);
  // Slides whose images have been requested. Only the current and next slides are
  // fetched up front, so the page doesn't download every photo on first load.
  const loadedSlides = useRef(new Set());
  loadedSlides.current.add(currentSlide);
  loadedSlides.current.add((currentSlide + 1) % slides.length);

  // Advance the slideshow
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, slides[currentSlide].duration);
    return () => clearTimeout(timer);
  }, [currentSlide]);

  // Only the visible video plays; the rest stay paused on their poster frame
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      if (index === currentSlide) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [currentSlide]);

  // Parallax background (desktop only)
  useEffect(() => {
    const media = window.matchMedia(MOTION_QUERY);
    let frame = null;

    const update = () => {
      frame = null;
      const background = backgroundRef.current;
      const section = sectionRef.current;
      if (!background || !section) return;

      if (!media.matches) {
        background.style.transform = "";
        return;
      }

      // Once the section starts scrolling off the top, the slideshow trails behind
      // at 30% of scroll speed; the section clips whatever slides past its bottom.
      const scrolledPast = Math.max(0, -section.getBoundingClientRect().top);
      background.style.transform = `translate3d(0, ${scrolledPast * 0.3}px, 0)`;
    };

    const handleScroll = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <section className="mission-section" ref={sectionRef} aria-label="Team photos">
      <div className="parallax-background" ref={backgroundRef}>
        {slides.map((slide, index) => (
          <div
            key={slide.image || slide.video}
            className={`background-slide ${
              index === currentSlide ? "active" : ""
            }`}
            aria-hidden={index !== currentSlide}
          >
            {slide.image ? (
              <div
                className="slide-image"
                role="img"
                aria-label={slide.alt}
                style={
                  loadedSlides.current.has(index)
                    ? { backgroundImage: `url("${slide.image}")` }
                    : undefined
                }
              />
            ) : (
              <video
                ref={(el) => (videoRefs.current[index] = el)}
                className="slide-video"
                poster={slide.poster}
                preload="none"
                muted
                loop
                playsInline
                aria-label={slide.alt}
              >
                <source src={slide.video} type="video/mp4" />
              </video>
            )}
          </div>
        ))}
        <div className="parallax-overlay" />
      </div>

      <div className="slide-indicators">
        {slides.map((slide, index) => (
          <button
            key={index}
            type="button"
            className={`indicator ${index === currentSlide ? "active" : ""}`}
            onClick={() => setCurrentSlide(index)}
            aria-label={`Show slide ${index + 1}: ${slide.alt}`}
            aria-current={index === currentSlide}
          />
        ))}
      </div>
    </section>
  );
};

export default Mission;
